import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Input, InputNumber, Modal, Switch } from 'antd';
import { appMessage as message } from '../utils/appMessage';
import { WarningOutlined } from '@ant-design/icons';

import { DashboardLayout } from '../layouts/DashboardLayout';
import {
  FONT_SIZE, FONT_WEIGHT, LINE_HEIGHT, RADIUS, SHADOW, SPACING,
} from '../constants/constants';
import { useThemeTokens } from '../hooks/UseThemeTokens';
import { STORAGE_KEY, useStorage } from '../contexts/StorageContext';
import { storeLabel } from '../constants/stores';
import { clearPricehubApiKey, fetchStoreSettings, saveStoreSettings, testPricehubConnection } from '../api/api';
import type { StoreSettingsUpdate, StoreSettingsView, PricehubTestResult } from '../types/settings';
import { AppButton } from '../components/common/AppButton';
import { AppTag } from '../components/common/AppTag';
import { AppPopconfirm } from '../components/common/AppPopconfirm';
import { AppLoadError } from '../components/common/AppLoadError';
import { useIsCompact } from '../hooks/UseIsCompact';

// ─── 패널 ─────────────────────────────────────────────────────────────────────

const Panel: React.FC<{ title: string; desc?: string; children: React.ReactNode }> = ({ title, desc, children }) => {
  const t = useThemeTokens();
  const compact = useIsCompact();
  return (
    <div style={{
      background: t.bgSurface, border: `1px solid ${t.border}`, borderRadius: RADIUS.lg,
      boxShadow: SHADOW.card,
      // 390px 에서는 좌우 24px 패딩이 카드 안 입력 폭을 342px 로 밀어내 입력이 카드 밖으로
      // 삐져나왔다 — 좁은 폭에서는 패딩을 한 단계 줄인다(2026-10-20 코덱 3차 지적).
      padding: compact ? SPACING.md : `${SPACING.lg}px ${SPACING.xl}px`,
    }}>
      <div style={{ fontSize: FONT_SIZE.md, fontWeight: FONT_WEIGHT.semibold, color: t.textPrimary }}>{title}</div>
      {desc && (
        <div style={{ fontSize: FONT_SIZE.sm, color: t.textSecondary, marginTop: SPACING.xxs, lineHeight: LINE_HEIGHT.relaxed }}>
          {desc}
        </div>
      )}
      <div style={{ marginTop: SPACING.md, display: 'flex', flexDirection: 'column', gap: SPACING.sm }}>{children}</div>
    </div>
  );
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => {
  const t = useThemeTokens();
  const compact = useIsCompact();
  // 좁은 폭에서는 128px 라벨 + 입력을 한 줄에 두면 입력이 카드 폭을 넘는다 — 라벨을 위로 올린다.
  return (
    <div style={{
      display: 'flex',
      flexDirection: compact ? 'column' : 'row',
      alignItems: compact ? 'stretch' : 'center',
      gap: compact ? SPACING.xxs : SPACING.sm,
    }}>
      <span style={{ fontSize: FONT_SIZE.sm, color: t.textSecondary, flex: 'none', width: compact ? 'auto' : 128 }}>{label}</span>
      <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
    </div>
  );
};

/**
 * PriceHub API Key 칸 아래 한 줄 — 값이 있는지 없는지(감사 card-controltower#96)와 어느 쪽 값을 쓰는지(#99).
 * 서버는 화면에 저장한 값(store_setting)이 있으면 그 값을, 없으면 서버 설정(yml · .env)을 쓴다. 응답에 출처
 * (pricehubApiKeySource)가 있으면 "화면 값 사용 중 / 서버 설정값 사용 중" 과 "화면 값 비우기" 를 보이고, 없는 예전
 * 서버에서는 있음 · 없음과 우선순위만 알린다(배포 순서가 바뀌어도 깨지지 않게).
 */
const ApiKeyStatus: React.FC<{
  view: StoreSettingsView;
  onClear: () => void;
  clearing: boolean;
  clearBlocked: boolean;
}> = ({ view, onClear, clearing, clearBlocked }) => {
  const t = useThemeTokens();
  const source = view.pricehubApiKeySource;
  const masked = view.pricehubApiKeyMasked ? ` (${view.pricehubApiKeyMasked})` : '';
  const missing = (
    <span style={{ color: t.danger, fontWeight: FONT_WEIGHT.semibold }}>
      API Key 없음 — 화면 값도 서버 설정값도 없어 PriceHub 연결이 실패합니다.
    </span>
  );
  let body: React.ReactNode;
  if (source === 'screen') {
    body = (
      <>
        <span style={{ color: t.success, fontWeight: FONT_WEIGHT.semibold }}>화면 값 사용 중{masked}</span>
        {' — 화면에서 저장한 키가 서버 설정값보다 우선합니다. 비우면 서버 설정값을 씁니다.'}
      </>
    );
  } else if (source === 'server') {
    body = (
      <>
        <span style={{ color: t.success, fontWeight: FONT_WEIGHT.semibold }}>서버 설정값 사용 중{masked}</span>
        {' — 화면에서 키를 저장하면 그 값이 우선합니다.'}
      </>
    );
  } else if (source === 'none') {
    body = missing;
  } else if (view.pricehubApiKeySet) {
    body = (
      <>
        <span style={{ color: t.success, fontWeight: FONT_WEIGHT.semibold }}>API Key 있음{masked}</span>
        {' — 화면에서 저장한 키가 있으면 서버 설정값보다 우선합니다.'}
      </>
    );
  } else {
    body = missing;
  }
  return (
    <div
      style={{
        fontSize: FONT_SIZE.xs, lineHeight: LINE_HEIGHT.normal, marginTop: SPACING.xxs, color: t.textMuted,
        display: 'flex', alignItems: 'center', gap: SPACING.xs, flexWrap: 'wrap',
      }}
    >
      <span>{body}</span>
      {source === 'screen' && (
        <AppPopconfirm
          title="화면 값 비우기"
          description="화면에서 저장한 PriceHub API Key 를 지웁니다. 그 뒤로는 서버 설정값을 쓰고, 서버 설정값도 없으면 PriceHub 연결이 실패합니다. 계속할까요?"
          okText="비우기"
          danger
          onConfirm={onClear}
        >
          <AppButton
            variant="ghost-danger"
            size="sm"
            loading={clearing}
            disabled={clearBlocked}
            disabledReason="설정을 다시 불러온 뒤 비울 수 있습니다"
          >
            화면 값 비우기
          </AppButton>
        </AppPopconfirm>
      )}
    </div>
  );
};

// ─── SettingsPage ────────────────────────────────────────────────────────────

interface FormState {
  pricehubBaseUrl: string;
  pricehubApiKey:  string; // '' = 기존 키 유지
  asPhone:         string;
  bundleGroupId:   number;
  visitAddressId:  number;
  shippingId:      number;
  returnId:        number;
}

const fromView = (v: StoreSettingsView): FormState => ({
  pricehubBaseUrl: v.pricehubBaseUrl,
  pricehubApiKey:  '',
  asPhone:         v.asPhone,
  bundleGroupId:   v.naverDeliveryBundleGroupId,
  visitAddressId:  v.naverVisitAddressId,
  shippingId:      v.naverShippingAddressId,
  returnId:        v.naverReturnAddressId,
});

/**
 * 다음 요청이 실제로 향할 매장 — http 인터셉터가 요청마다 localStorage 의 superStore 를 X-Store-Code 로 싣고(SUPER), 매장 계정은
 * 로그인 매장이 대상이다. 다른 탭에서 SUPER 가 매장을 바꾸면 이 탭의 화면 상태(useStorage)는 그대로인데 요청은 새 매장으로 나간다
 * (감사 card-controltower#310).
 */
const requestStore = (): string | null =>
  localStorage.getItem(STORAGE_KEY.store) ?? localStorage.getItem(STORAGE_KEY.superStore);

const storeChangedMessage = (now: string | null) =>
  `매장이 바뀌어 저장하지 않았습니다(지금 ${now ? storeLabel(now) : '매장 미선택'}) — 새로고침해 그 매장 설정을 확인한 뒤 다시 시도하세요.`;

/** 응답이 그 매장 것인가 — 응답의 storeCode 와 대조한다(예전 서버처럼 비어 있으면 믿는다). 다른 탭 매장 전환 뒤의 조회 대비(#310). */
const viewIsFor = (v: StoreSettingsView, store: string | null): boolean =>
  !v.storeCode || (store != null && v.storeCode.toUpperCase() === store.toUpperCase());

const STORE_MISMATCH_ERROR = '다른 탭에서 매장이 바뀌어 이 매장 설정을 받지 못했습니다 — 새로고침한 뒤 다시 확인하세요';

const toPayload = (f: FormState): StoreSettingsUpdate => ({
  pricehubBaseUrl:            f.pricehubBaseUrl.trim(),
  pricehubApiKey:             f.pricehubApiKey.trim(),
  asPhone:                    f.asPhone.trim(),
  naverDeliveryBundleGroupId: f.bundleGroupId,
  naverVisitAddressId:        f.visitAddressId,
  naverShippingAddressId:     f.shippingId,
  naverReturnAddressId:       f.returnId,
});

/**
 * 매장 설정(2026-09-01) — PriceHub 서버·API Key, 상품 등록 A/S 전화번호, 네이버 출고지/
 * 반품지 주소 ID를 매장별로 편집한다. 원래 백엔드 코드에 하드코딩돼 있던 값.
 *
 * 대상 매장은 백엔드가 정한다(매장 ADMIN=자기 매장, SUPER=선택한 매장). SUPER가 매장을
 * 고르지 않았으면 먼저 고르라고 안내한다.
 */
export const SettingsPage: React.FC = () => {
  const t = useThemeTokens();
  const { isSuper, store, superStore } = useStorage();
  const targetStore = store ?? superStore;

  const [view, setView]     = useState<StoreSettingsView | null>(null);
  const [form, setForm]     = useState<FormState | null>(null);
  const [loading, setLoading] = useState(true);
  // 첫 조회 실패는 토스트만 띄우고 사라져서 화면엔 빈 입력칸만 남았다 — 본문에 오류와
  // 다시 시도를 남긴다(2026-10-20 코덱 3차 지적).
  const [error, setError]   = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<PricehubTestResult | null>(null);
  const [clearing, setClearing] = useState(false);
  const [savingAuto, setSavingAuto] = useState(false);
  // 지금 화면의 매장 — 비우기 응답이 오기 전에 SUPER 가 매장을 바꾸면 그 응답(앞 매장의 키 상태)을 버린다(Opus 리뷰 PR #50 중간).
  const storeRef = useRef(targetStore);
  storeRef.current = targetStore;
  // 지금 view · form 을 받은 매장(감사 card-controltower#310) — 저장 직전에 이 값 · 화면 매장 · 다음 요청의 매장이 모두 같아야 보낸다.
  const loadedStoreRef = useRef<string | null>(null);

  /**
   * 저장 직전 매장 재확인(감사 card-controltower#310) — 보낼 값을 받은 매장(expected)이 화면 매장 · 다음 요청의 매장과 다르면 앞 매장의
   * 설정 전체가 다른 매장에 저장된다. 다르면 보내지 않고 안내한다(true = 중단).
   */
  const storeMoved = useCallback((expected: string | null): boolean => {
    const now = requestStore();
    if (expected != null && now === expected && storeRef.current === expected && loadedStoreRef.current === expected) {
      return false;
    }
    message.warning({ content: storeChangedMessage(now), duration: 8 });
    return true;
  }, []);

  const load = useCallback(() => {
    if (!targetStore) { setLoading(false); return; }
    const startedFor = targetStore;
    setLoading(true);
    setError(null);
    // 매장을 바꾼 뒤 앞 매장의 늦은 응답이 새 매장 화면을 덮지 않게 한다 — 그 값이 새 매장에 저장되는 길이다(#310 같은 유형).
    fetchStoreSettings()
      .then((v) => {
        if (storeRef.current !== startedFor) return;
        // 다른 탭에서 매장을 바꿨으면 이 조회는 그 매장 값으로 왔다 — 이 매장 값으로 기록하면 나중에 이 매장에 저장된다(코덱 셀프 리뷰 높음).
        if (!viewIsFor(v, startedFor)) { setError(STORE_MISMATCH_ERROR); return; }
        loadedStoreRef.current = startedFor;
        setView(v);
        setForm(fromView(v));
      })
      .catch((err) => { if (storeRef.current === startedFor) setError(err instanceof Error ? err.message : String(err)); })
      .finally(() => { if (storeRef.current === startedFor) setLoading(false); });
  }, [targetStore]);

  useEffect(() => { load(); }, [load]);

  const patch = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => (f ? { ...f, [key]: value } : f));

  type NumKey = 'bundleGroupId' | 'visitAddressId' | 'shippingId' | 'returnId';
  const patchNum = (key: NumKey, v: number | null) =>
    setForm((f) => (f ? { ...f, [key]: v ?? 0 } : f));

  const doSave = useCallback(async (): Promise<boolean> => {
    if (!form) return false;
    // 다른 탭에서 매장이 바뀌었으면 이 form(앞 매장 값)을 새 매장에 저장하지 않는다(감사 card-controltower#310).
    const savingFor = loadedStoreRef.current;
    if (storeMoved(savingFor)) return false;
    setSaving(true);
    try {
      const next = await saveStoreSettings(toPayload(form));
      if (storeRef.current !== savingFor) return false; // 응답 전에 매장을 바꿨다 — 새 매장 화면을 덮지 않는다
      if (!viewIsFor(next, savingFor)) { setError(STORE_MISMATCH_ERROR); return false; }
      setView(next);
      setForm(fromView(next)); // apiKey 입력칸 비우고 마스킹 갱신
      message.success('저장했습니다');
      return true;
    } catch {
      if (storeRef.current === savingFor) message.error('저장에 실패했습니다');
      return false;
    } finally {
      setSaving(false);
    }
  }, [form, storeMoved]);

  // 가격 상승분 자동 반영 스위치(감사 card-controltower#146) — 바로 저장한다. 다른 칸은 마지막으로 받은 서버 값(view)을 그대로
  // 보내 입력 중인 다른 칸을 함께 저장하지 않는다(API Key 는 '' = 유지).
  // openedFor = 이 view 를 받은 매장(확인 창을 연 때 잡아 둔다) — 확인 창이 열린 동안 매장이 바뀌면 이 view(앞 매장 설정 전체)를
  // 새 매장에 저장하게 된다(감사 card-controltower#310). 저장 직전에 다시 비교해 다르면 중단 · 안내한다.
  const saveAuto = useCallback(async (on: boolean, openedFor: string | null) => {
    // 다른 저장 · 연결 테스트 · 비우기가 도는 동안은 보내지 않는다 — 옛 view 전체를 보내는 요청이 그 결과를 덮었다(코덱 셀프 리뷰 중간).
    if (!view || savingAuto || saving || testing || clearing) return;
    if (storeMoved(openedFor)) return;
    setSavingAuto(true);
    try {
      const next = await saveStoreSettings({ ...toPayload(fromView(view)), autoPriceIncreaseEnabled: on });
      if (storeRef.current !== openedFor) return; // 응답 전에 매장을 바꿨다 — 새 매장 화면을 덮지 않는다
      if (!viewIsFor(next, openedFor)) { setError(STORE_MISMATCH_ERROR); return; }
      setView(next);
      message.success(on ? '가격 상승분 자동 반영을 켰습니다' : '가격 상승분 자동 반영을 껐습니다');
    } catch {
      if (storeRef.current === openedFor) message.error('자동 반영 설정을 저장하지 못했습니다');
    } finally {
      setSavingAuto(false);
    }
  }, [view, savingAuto, saving, testing, clearing, storeMoved]);

  const toggleAuto = useCallback((on: boolean) => {
    const openedFor = loadedStoreRef.current;
    if (!on) { void saveAuto(false, openedFor); return; }
    // 켤 때는 무인으로 실제 판매가가 바뀌므로 조건을 보여 주고 한 번 더 묻는다.
    Modal.confirm({
      title: '가격 상승분 자동 반영을 켤까요?',
      okText: '켜기',
      cancelText: '취소',
      content: (
        <div>
          <p style={{ marginTop: 0 }}>켜면 매일 정해진 시각에 아래 조건을 모두 통과한 <b>가격 상승분만</b> 네이버 판매가에 자동으로 반영합니다.</p>
          <ul style={{ paddingLeft: 18, marginBottom: 0 }} data-testid="auto-conditions">
            <li>신선도 — PriceHub 값이 24시간 안에 받은 것</li>
            <li>상승률 — 50% 를 넘게 오른 카드는 빼고 &apos;확인 필요&apos; 로 남김</li>
            <li>출처 — 이 매장으로 인식된 값(매장 가격)만, PriceHub 가격으로 대신 받은 값은 제외</li>
          </ul>
        </div>
      ),
      onOk: () => saveAuto(true, openedFor),
    });
  }, [saveAuto]);

  // 화면 값 비우기(감사 card-controltower#99) — 입력칸에 치던 다른 값(서버 주소 등)은 건드리지 않고 키 상태만 갱신한다.
  const clearKey = useCallback(async () => {
    if (clearing) return;
    const startedFor = storeRef.current;
    const stale = () => storeRef.current !== startedFor;
    // 다른 탭에서 매장이 바뀌었으면 다른 매장의 키를 지우게 된다(감사 card-controltower#310 같은 유형).
    if (storeMoved(startedFor)) return;
    setClearing(true);
    try {
      let next: StoreSettingsView | null;
      try {
        next = await clearPricehubApiKey();
      } catch {
        if (!stale()) message.error('API Key 를 비우지 못했습니다');
        return;
      }
      if (stale()) return;
      message.success('화면에서 저장한 API Key 를 비웠습니다 — 이제 서버 설정값을 씁니다');
      setTestResult(null);
      if (!next) {
        // 본문 없이(204) 끝났으면 새 상태를 다시 읽는다 — 비우기는 이미 됐으니 읽기 실패는 따로 알린다.
        try {
          next = await fetchStoreSettings();
          if (stale()) return;
        } catch {
          if (stale()) return;
          // 키는 이미 지워졌다 — 옛 출처 표시 · 입력값으로 다시 저장하지 않게 오류 상태로 두고(저장 막힘) 다시 불러오기를 띄운다
          // (코덱 셀프 리뷰 PR #50 중간).
          setError('API Key 는 비웠지만 설정을 다시 읽지 못했습니다');
          return;
        }
      }
      const fresh = next;
      // 204 뒤 다시 읽기는 지금 X-Store-Code 로 나간다 — 그 사이 다른 탭에서 매장이 바뀌었으면 다른 매장 값이 온다(코덱 셀프 리뷰 높음).
      // 이 매장 값으로 기록하지 않고 오류 상태로 두어 저장을 막는다.
      if (!viewIsFor(fresh, startedFor)) { setError(STORE_MISMATCH_ERROR); return; }
      loadedStoreRef.current = startedFor;
      setView(fresh);
      setForm((f) => (f ? { ...f, pricehubApiKey: '' } : fromView(fresh)));
    } finally {
      setClearing(false);
    }
  }, [clearing, storeMoved]);

  const saveAndTest = useCallback(async () => {
    const ok = await doSave();
    if (!ok) return;
    const testedFor = storeRef.current;
    setTesting(true);
    setTestResult(null);
    try {
      const result = await testPricehubConnection();
      if (storeRef.current === testedFor) setTestResult(result);
    } catch {
      if (storeRef.current === testedFor) setTestResult({ ok: false, message: '테스트 요청을 보내지 못했습니다' });
    } finally {
      setTesting(false);
    }
  }, [doSave]);

  // 재조회가 실패한 채로 남은 form 은 서버의 최신 값이 아닐 수 있다 — 그 상태로 저장하면 그 사이
  // 다른 곳에서 바뀐 설정을 낡은 값으로 덮는다. 다시 불러오기에 성공할 때까지 저장을 막는다
  // (2026-10-20 리뷰 후속, 감사 #47).
  // 비우기(DELETE)가 도는 동안 저장(PUT)을 막는다 — 두 요청이 겹치면 응답 순서에 따라 새로 저장한 키가 지워지거나 늦은 비우기
  // 응답이 최신 표시를 덮는다(코덱 셀프 리뷰 PR #50 중간).
  const saveBlocked = loading || !form || !!error || clearing || savingAuto;
  const saveBlockedReason = error
    ? '설정을 다시 불러온 뒤 저장할 수 있습니다'
    : clearing ? 'API Key 를 비우는 중입니다'
      : savingAuto ? '자동 반영 설정을 저장하는 중입니다' : undefined;
  // 저장이 막힌 동안(다시 불러오기 실패 · 비우는 중)에는 입력칸도 잠근다 — 저장만 막히고 입력은 되면 고친 값이 저장될 것처럼
  // 보였다(감사 card-controltower#49).
  const inputLocked = loading || !!error || clearing;

  const apiKeyPlaceholder = useMemo(
    () => (view?.pricehubApiKeySet ? `저장된 키 유지 (${view.pricehubApiKeyMasked ?? '••••'}) — 바꿀 때만 입력` : 'API Key 입력'),
    [view],
  );

  const numberField = (label: string, key: NumKey) => (
    <Field label={label}>
      <InputNumber
        min={0}
        value={form ? form[key] : undefined}
        onChange={(v) => patchNum(key, v)}
        disabled={inputLocked}
        style={{ width: '100%', maxWidth: 220 }}
      />
    </Field>
  );

  // ── SUPER 매장 미선택 ──
  if (isSuper && !targetStore) {
    return (
      <DashboardLayout title="설정">
        <div style={{
          background: t.bgSurface, border: `1px solid ${t.border}`, borderRadius: RADIUS.lg,
          boxShadow: SHADOW.card, padding: `${SPACING.xxl}px ${SPACING.lg}px`, textAlign: 'center',
        }}>
          <div style={{ fontSize: FONT_SIZE.md, fontWeight: FONT_WEIGHT.semibold, color: t.textPrimary }}>
            매장을 먼저 선택하세요
          </div>
          <div style={{ fontSize: FONT_SIZE.sm, color: t.textMuted, marginTop: SPACING.xs }}>
            설정은 매장별로 따로 관리됩니다. 왼쪽 사이드바에서 부산점/광주점을 고르면 그 매장 설정이 열립니다.
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="설정">
      <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.sm, marginBottom: SPACING.lg, flexWrap: 'wrap' }}>
        <span style={{ fontSize: FONT_SIZE.lg, fontWeight: FONT_WEIGHT.bold, color: t.textPrimary }}>
          {targetStore ? `${storeLabel(targetStore)} 설정` : '매장 설정'}
        </span>
        {view?.usingDefaults && <AppTag variant="manual" />}
        {view?.usingDefaults && (
          <span style={{ fontSize: FONT_SIZE.xs, color: t.textMuted }}>저장된 값이 없어 기본값을 보여줍니다</span>
        )}
      </div>

      {/* 재조회 실패 — 이전 값이 남아 있으면 표를 유지한 채 한 줄로, 첫 조회 실패면 본문에
          크게 안내한다(둘 다 다시 시도 버튼 포함). */}
      {error && (
        <div style={{ marginBottom: SPACING.md }}>
          {form
            ? <AppLoadError inline message={error} onRetry={load} />
            : <AppLoadError message={error} onRetry={load} />}
        </div>
      )}

      {(!error || form) && (
      <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING.md, maxWidth: 720 }}>
        <Panel
          title="PriceHub 연결"
          desc="pricehub 서버 주소와 발급받은 API Key. API Key는 서버에서 python manage.py shell → APIKey.objects.create_key(name='...')로 발급합니다."
        >
          <Field label="서버 주소">
            <Input
              value={form?.pricehubBaseUrl ?? ''}
              onChange={(e) => patch('pricehubBaseUrl', e.target.value)}
              placeholder="http://..."
              disabled={inputLocked}
            />
          </Field>
          <Field label="API Key">
            <Input.Password
              value={form?.pricehubApiKey ?? ''}
              onChange={(e) => patch('pricehubApiKey', e.target.value)}
              placeholder={apiKeyPlaceholder}
              disabled={inputLocked}
              autoComplete="new-password"
            />
            {/* 오류 상태에서는 옛 키 상태가 지금과 다를 수 있어 숨긴다 — 위 다시 불러오기로 새로 읽는다. */}
            {view && !error && <ApiKeyStatus view={view} onClear={clearKey} clearing={clearing} clearBlocked={(saveBlocked && !clearing) || saving || testing} />}
          </Field>
          <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.xs, marginTop: SPACING.xxs, flexWrap: 'wrap' }}>
            <AppButton variant="primary" loading={saving} disabled={saveBlocked} disabledReason={saveBlockedReason} onClick={doSave}>저장</AppButton>
            <AppButton variant="default" loading={testing || saving} disabled={saveBlocked} disabledReason={saveBlockedReason} onClick={saveAndTest}>
              저장 후 연결 테스트
            </AppButton>
            {testResult && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: SPACING.xxs, flexWrap: 'wrap',
                fontSize: FONT_SIZE.sm, color: testResult.ok ? t.success : t.danger,
              }}>
                {testResult.ok ? <span>✓</span> : <WarningOutlined />}
                <span>{testResult.message}</span>
              </span>
            )}
          </div>
        </Panel>

        <Panel
          title="가격 상승분 자동 반영"
          desc="매장별 스위치(기본 꺼짐). 켜면 매일 정해진 시각에 조건을 통과한 가격 상승분만 네이버에 자동 반영합니다. 하락은 자동 반영하지 않습니다."
        >
          {view && view.autoPriceIncreaseEnabled === undefined ? (
            <span style={{ fontSize: FONT_SIZE.sm, color: t.textMuted }}>서버가 아직 이 설정을 지원하지 않습니다(서버 업데이트 뒤 사용).</span>
          ) : (
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: SPACING.xs, minHeight: 44, cursor: 'pointer' }}>
              <Switch
                checked={!!view?.autoPriceIncreaseEnabled}
                loading={savingAuto}
                disabled={saveBlocked || saving || testing || clearing || savingAuto}
                onChange={toggleAuto}
                aria-label="가격 상승분 자동 반영"
              />
              <span style={{ fontSize: FONT_SIZE.sm, color: t.textPrimary }}>
                {view?.autoPriceIncreaseEnabled ? '켜짐 — 매일 조건을 통과한 상승분을 자동 반영' : '꺼짐'}
              </span>
            </label>
          )}
        </Panel>

        <Panel
          title="상품 등록 기본값"
          desc="신규 상품을 네이버 스토어에 등록할 때 쓰는 매장 정보. 주소 ID는 네이버 판매자센터에 등록된 출고지/반품지의 ID입니다."
        >
          <Field label="A/S 전화번호">
            <Input
              value={form?.asPhone ?? ''}
              onChange={(e) => patch('asPhone', e.target.value)}
              placeholder="010-0000-0000"
              disabled={inputLocked}
              style={{ width: '100%', maxWidth: 220 }}
            />
          </Field>
          {numberField('묶음배송그룹 ID', 'bundleGroupId')}
          {numberField('방문수령지 ID', 'visitAddressId')}
          {numberField('출고지 ID', 'shippingId')}
          {numberField('반품지 ID', 'returnId')}
          <div style={{ marginTop: SPACING.xxs }}>
            <AppButton variant="primary" loading={saving} disabled={saveBlocked} disabledReason={saveBlockedReason} onClick={doSave}>저장</AppButton>
          </div>
        </Panel>
      </div>
      )}
    </DashboardLayout>
  );
};
